/**
 * Workspace Catalog — localStorage-кэш справочников участков, пользователей и процедур.
 *
 * Цель: устранить мелькание числовых ID при первом рендере страниц.
 * Механизм:
 *  1. API-функции getWorkspaceAreas / getWorkspaceUsers / getWorkspaceScenarios
 *     вызывают cacheAreas / cacheUsers / cacheScenarios в качестве side-effect
 *     после успешного ответа сервера.
 *  2. Компоненты читают кэш синхронно через lookupXxx при монтировании,
 *     получая имя немедленно — без ожидания API.
 *  3. При обновлении кэша диспетчеризуется кастомный DOM-event «ws-catalog-updated»,
 *     чтобы уже смонтированные компоненты смогли перерисоваться.
 *
 * Кэш хранится в localStorage под ключом «ws_catalog_{workspaceId}».
 * TTL: 60 минут — достаточно для одной рабочей сессии.
 */

export interface UserCatalogEntry {
  name: string
  login: string
}

interface WorkspaceCatalog {
  areas: Record<number, string>             // id → name
  users: Record<number, UserCatalogEntry>   // userId → { name, login }
  scenarios: Record<number, string>         // id → name
  ts: number                                // unix ms последнего обновления
}

const CATALOG_TTL_MS = 60 * 60 * 1000 // 1 час
const CATALOG_EVENT = "ws-catalog-updated"

function catalogKey(workspaceId: number): string {
  return `ws_catalog_${workspaceId}`
}

function readCatalog(workspaceId: number): WorkspaceCatalog {
  try {
    if (typeof localStorage === "undefined") return emptyCatalog()
    const raw = localStorage.getItem(catalogKey(workspaceId))
    if (!raw) return emptyCatalog()
    const parsed: WorkspaceCatalog = JSON.parse(raw)
    if (Date.now() - (parsed.ts ?? 0) > CATALOG_TTL_MS) return emptyCatalog()
    return parsed
  } catch {
    return emptyCatalog()
  }
}

function emptyCatalog(): WorkspaceCatalog {
  return { areas: {}, users: {}, scenarios: {}, ts: 0 }
}

function writeCatalog(workspaceId: number, catalog: WorkspaceCatalog): void {
  try {
    if (typeof localStorage === "undefined") return
    localStorage.setItem(catalogKey(workspaceId), JSON.stringify({ ...catalog, ts: Date.now() }))
  } catch {
    // Игнорируем ошибки записи (приватный режим, квота и т.п.)
  }
}

/** Инвалидировать кэш конкретного workspace (при смене аккаунта / workspace). */
export function invalidateCatalog(workspaceId: number): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(catalogKey(workspaceId))
    }
  } catch {}
}

// ─── Populate helpers (вызываются из API side-effects) ────────────────────────

/** Сохранить список участков в кэш. */
export function cacheAreas(
  workspaceId: number,
  areas: Array<{ id: number; name: string }>
): void {
  const catalog = readCatalog(workspaceId)
  areas.forEach(a => { catalog.areas[a.id] = a.name })
  writeCatalog(workspaceId, catalog)
  notifyCatalogUpdated()
}

/** Сохранить список пользователей workspace в кэш. */
export function cacheUsers(
  workspaceId: number,
  users: Array<{ userId: number; user?: { name: string; login: string } | null }>
): void {
  const catalog = readCatalog(workspaceId)
  users.forEach(uw => {
    if (uw.user) {
      catalog.users[uw.userId] = { name: uw.user.name, login: uw.user.login }
    }
  })
  writeCatalog(workspaceId, catalog)
  notifyCatalogUpdated()
}

/** Сохранить один пользователь (например, из nested-объекта инцидента). */
export function cacheUser(
  workspaceId: number,
  userId: number,
  user: { name: string; login: string }
): void {
  const catalog = readCatalog(workspaceId)
  catalog.users[userId] = { name: user.name, login: user.login }
  writeCatalog(workspaceId, catalog)
  // не диспетчеризуем событие чтобы не перерисовывать всё при каждом инциденте
}

/** Сохранить список процедур в кэш. */
export function cacheScenarios(
  workspaceId: number,
  scenarios: Array<{ id: number; name: string }>
): void {
  const catalog = readCatalog(workspaceId)
  scenarios.forEach(s => { catalog.scenarios[s.id] = s.name })
  writeCatalog(workspaceId, catalog)
  notifyCatalogUpdated()
}

// ─── Lookup helpers (вызываются синхронно при рендере) ───────────────────────

/** Вернуть название участка из кэша или null если нет. */
export function lookupAreaName(workspaceId: number, id: number): string | null {
  if (!id || !workspaceId) return null
  return readCatalog(workspaceId).areas[id] ?? null
}

/** Вернуть данные пользователя из кэша или null если нет. */
export function lookupUserEntry(
  workspaceId: number,
  userId: number
): UserCatalogEntry | null {
  if (!userId || !workspaceId) return null
  return readCatalog(workspaceId).users[userId] ?? null
}

/** Вернуть имя пользователя из кэша (displayName = name или login) или null. */
export function lookupUserName(workspaceId: number, userId: number): string | null {
  const entry = lookupUserEntry(workspaceId, userId)
  if (!entry) return null
  return entry.name || entry.login || null
}

/** Вернуть название процедуры из кэша или null если нет. */
export function lookupScenarioName(workspaceId: number, id: number): string | null {
  if (!id || !workspaceId) return null
  return readCatalog(workspaceId).scenarios[id] ?? null
}

// ─── Bulk reads (для pre-fill dropdown'ов) ───────────────────────────────────

/** Все кэшированные участки workspace в виде массива {id, name}. */
export function getAllCachedAreas(workspaceId: number): Array<{ id: number; name: string }> {
  const catalog = readCatalog(workspaceId)
  return Object.entries(catalog.areas).map(([id, name]) => ({ id: Number(id), name }))
}

/** Все кэшированные пользователи workspace в виде массива. */
export function getAllCachedUsers(workspaceId: number): Array<{ userId: number } & UserCatalogEntry> {
  const catalog = readCatalog(workspaceId)
  return Object.entries(catalog.users).map(([id, entry]) => ({ userId: Number(id), ...entry }))
}

/** Все кэшированные процедуры workspace в виде массива {id, name}. */
export function getAllCachedScenarios(workspaceId: number): Array<{ id: number; name: string }> {
  const catalog = readCatalog(workspaceId)
  return Object.entries(catalog.scenarios).map(([id, name]) => ({ id: Number(id), name }))
}

// ─── DOM event ───────────────────────────────────────────────────────────────

export const WS_CATALOG_UPDATED_EVENT = CATALOG_EVENT

function notifyCatalogUpdated(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CATALOG_EVENT))
  }
}
